import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { getContactListById } from "../../services/purecloud";

interface ColumnSelectionDialogProps {
  open: boolean;
  contactListId: string | null | undefined;
  initialSelectedColumns: string[];
  onClose: () => void;
  onConfirm: (selectedColumns: string[]) => void;
}

const ColumnSelectionDialog = ({
  open,
  contactListId,
  initialSelectedColumns,
  onClose,
  onConfirm,
}: ColumnSelectionDialogProps) => {
  const [columns, setColumns] = useState<string[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch columns whenever the dialog opens with a contactListId
  useEffect(() => {
    if (open && contactListId) {
      setIsLoading(true);
      setError(null);
      getContactListById(contactListId)
        .then((data: any) => {
          const cols: string[] = data?.columnNames || [];
          setColumns(cols);
          // Restore previously selected columns if any, otherwise select all by default
          const validSelected = initialSelectedColumns.filter((c) =>
            cols.includes(c)
          );
          setSelectedColumns(validSelected.length > 0 ? validSelected : [...cols]);
        })
        .catch(() => {
          setError("Failed to load columns. Please try again.");
          setColumns([]);
        })
        .finally(() => setIsLoading(false));
    }
  }, [open, contactListId]);

  const allSelected =
    columns.length > 0 && selectedColumns.length === columns.length;
  const someSelected =
    selectedColumns.length > 0 && selectedColumns.length < columns.length;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns([...columns]);
    }
  };

  const handleToggle = (column: string) => {
    setSelectedColumns((prev) =>
      prev.includes(column)
        ? prev.filter((c) => c !== column)
        : [...prev, column]
    );
  };

  const handleConfirm = () => {
    onConfirm(selectedColumns);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select Columns to Export</DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}
        {!isLoading && error && (
          <Typography color="error">{error}</Typography>
        )}
        {!isLoading && !error && columns.length === 0 && (
          <Typography color="text.secondary">No columns available.</Typography>
        )}
        {!isLoading && !error && columns.length > 0 && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleSelectAll}
                />
              }
              label={
                <Typography fontWeight={600}>
                  {allSelected ? "Deselect All" : "Select All"} ({selectedColumns.length}/{columns.length})
                </Typography>
              }
            />
            <Divider sx={{ mb: 1 }} />
            <Box
              sx={{
                maxHeight: 360,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {columns.map((col) => (
                <FormControlLabel
                  key={col}
                  control={
                    <Checkbox
                      checked={selectedColumns.includes(col)}
                      onChange={() => handleToggle(col)}
                      size="small"
                    />
                  }
                  label={col}
                />
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoading}
          sx={{ color: "white", backgroundColor: "#1b2c48" }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnSelectionDialog;
